import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Text,
  Textarea,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';

// YYYY-MM-DDTHH:MM  ←→  YYYYMMDDTHHMMSS
const toInputDate = (raw) => {
  if (!raw) return '';
  // raw = YYYYMMDDTHHMMSS or YYYYMMDDTHHMMSS
  const s = raw.replace('T', '');
  const y = s.slice(0, 4);
  const mo = s.slice(4, 6);
  const d = s.slice(6, 8);
  if (!y || !mo || !d) return '';
  return `${y}-${mo}-${d}`;
};

const toInputTime = (raw) => {
  if (!raw) return '';
  const t = raw.split('T')[1] || '';
  const h = t.slice(0, 2) || '00';
  const m = t.slice(2, 4) || '00';
  return `${h}:${m}`;
};

const fromParts = (date, time) => {
  if (!date) return '';
  const [y, mo, d] = date.split('-');
  const [h, m] = (time || '00:00').split(':');
  return `${y}${mo}${d}T${h}${m}00`;
};

const DateTimeRow = ({ label, dateValue, timeValue, onDateChange, onTimeChange, inputBg, borderColor, labelColor }) => (
  <Box>
    <Text fontSize="xs" fontWeight="medium" color={labelColor} mb={1.5}>
      {label}
    </Text>
    <Grid templateColumns="1fr 1fr" gap={2}>
      <FormControl>
        <Input
          type="date"
          value={dateValue}
          onChange={(e) => onDateChange(e.target.value)}
          size="sm"
          borderRadius="lg"
          bg={inputBg}
          borderColor={borderColor}
          sx={{
            colorScheme: 'light',
            '&::-webkit-calendar-picker-indicator': { cursor: 'pointer', opacity: 0.6 },
          }}
        />
      </FormControl>
      <FormControl>
        <Input
          type="time"
          value={timeValue}
          onChange={(e) => onTimeChange(e.target.value)}
          size="sm"
          borderRadius="lg"
          bg={inputBg}
          borderColor={borderColor}
          sx={{
            '&::-webkit-calendar-picker-indicator': { cursor: 'pointer', opacity: 0.6 },
          }}
        />
      </FormControl>
    </Grid>
  </Box>
);

const QrEventFields = observer(({ store }) => {
  const mutedBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.600', 'gray.300');

  const fd = store.formData;

  const setField = (id, val) => store.setFormData(id, val);

  const startDate = toInputDate(fd.start || '');
  const startTime = toInputTime(fd.start || '');
  const endDate = toInputDate(fd.end || '');
  const endTime = toInputTime(fd.end || '');

  const onStartDate = (v) => setField('start', fromParts(v, startTime));
  const onStartTime = (v) => setField('start', fromParts(startDate, v));
  const onEndDate = (v) => setField('end', fromParts(v, endTime));
  const onEndTime = (v) => setField('end', fromParts(endDate, v));

  return (
    <Box mt={3} p={4} borderRadius="lg" bg={mutedBg} display="flex" flexDirection="column" gap={4}>
      {/* Title */}
      <FormControl isRequired>
        <FormLabel fontSize="xs" mb={1} color={labelColor}>Event title</FormLabel>
        <Input
          value={fd.title ?? ''}
          onChange={(e) => setField('title', e.target.value)}
          placeholder="Team meeting"
          size="sm"
          borderRadius="lg"
          bg={inputBg}
          autoFocus
        />
      </FormControl>

      {/* Start */}
      <DateTimeRow
        label="Starts"
        dateValue={startDate}
        timeValue={startTime}
        onDateChange={onStartDate}
        onTimeChange={onStartTime}
        inputBg={inputBg}
        borderColor={borderColor}
        labelColor={labelColor}
      />

      {/* End */}
      <DateTimeRow
        label="Ends"
        dateValue={endDate}
        timeValue={endTime}
        onDateChange={onEndDate}
        onTimeChange={onEndTime}
        inputBg={inputBg}
        borderColor={borderColor}
        labelColor={labelColor}
      />

      {/* Location */}
      <FormControl>
        <FormLabel fontSize="xs" mb={1} color={labelColor}>Location (optional)</FormLabel>
        <Input
          value={fd.location ?? ''}
          onChange={(e) => setField('location', e.target.value)}
          placeholder="Conference Room A"
          size="sm"
          borderRadius="lg"
          bg={inputBg}
        />
      </FormControl>

      {/* Description */}
      <FormControl>
        <FormLabel fontSize="xs" mb={1} color={labelColor}>Description (optional)</FormLabel>
        <Textarea
          value={fd.description ?? ''}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="Event details..."
          size="sm"
          borderRadius="lg"
          rows={2}
          bg={inputBg}
          resize="vertical"
        />
      </FormControl>
    </Box>
  );
});

export default QrEventFields;
